/**
 * PhoneInput — country-aware mobile number field (REG-13).
 *
 * Extracted from the inline picker in AnonymousReport.jsx so PTW and other
 * forms share one set of per-country rules (see MOBILE_COUNTRY_OPTIONS in
 * utils/inputSanitizers.js — India 10, Singapore 8, Malaysia 9–11 digits…).
 *
 * VALUE FORMAT
 *   The value is the NATIONAL number only — the country code is never added,
 *   so stored values keep the same shape they always had (e.g. a PTW mobile
 *   is still plain digits). The selected country drives the digit cap and the
 *   validation message only.
 *   - digitsOnly (default true): non-digits are stripped and the value is
 *     capped at the country's max digits as the user types.
 *   - digitsOnly={false}: the value is passed through exactly as typed (up to
 *     `maxLength` characters) — AnonymousReport uses this so its submitted
 *     payload is unchanged.
 *
 * Props
 *   value, onChange(value)           controlled national number
 *   country, onCountryChange(code)   optional controlled country; when
 *                                     omitted the component keeps its own
 *   defaultCountry                   initial country when uncontrolled;
 *                                     defaults to the tenant country, else IN
 *   showCountrySelect (true)         render the country <select>
 *   required, disabled, name, id, placeholder, maxLength
 *   error                            external error message (shown in red)
 *   validateOnBlur (true)            show the country rule message on blur
 *   inputClassName, selectClassName, className
 */

import React, { useState } from 'react';
import {
  MOBILE_COUNTRY_OPTIONS, mobileRules, validateMobile,
} from '../utils/inputSanitizers';
import { getTenantCountry } from '../utils/regional';

/** Tenant country if it has phone rules, else India (legacy behaviour). */
export const defaultPhoneCountry = () => {
  const c = getTenantCountry();
  return MOBILE_COUNTRY_OPTIONS.some((o) => o.code === c) ? c : 'IN';
};

const DEFAULT_INPUT_CLASS =
  'w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none';
const DEFAULT_SELECT_CLASS =
  'rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0';

const PhoneInput = ({
  value,
  onChange,
  country: controlledCountry,
  onCountryChange,
  defaultCountry,
  showCountrySelect = true,
  digitsOnly = true,
  required = false,
  disabled = false,
  name,
  id,
  placeholder,
  maxLength,
  error,
  validateOnBlur = true,
  className = '',
  inputClassName,
  selectClassName,
}) => {
  const [internalCountry, setInternalCountry] = useState(
    () => defaultCountry || defaultPhoneCountry(),
  );
  const [touched, setTouched] = useState(false);
  const country = controlledCountry || internalCountry;
  const rules = mobileRules(country);

  const setCountry = (code) => {
    if (!controlledCountry) setInternalCountry(code);
    if (onCountryChange) onCountryChange(code);
    if (digitsOnly && value) {
      // Re-cap an existing value to the new country's maximum.
      const capped = String(value).replace(/[^0-9]/g, '').slice(0, mobileRules(code).maxDigits);
      if (capped !== value) onChange(capped);
    }
  };

  const handleInput = (e) => {
    const raw = e.target.value;
    onChange(digitsOnly ? raw.replace(/[^0-9]/g, '').slice(0, rules.maxDigits) : raw);
  };

  const ruleError = validateOnBlur && touched ? validateMobile(value, country) : null;
  const shownError = error || ruleError;
  const borderClass = shownError ? 'border-red-300' : 'border-gray-300';

  return (
    <div className={className}>
      <div className="flex gap-2">
        {showCountrySelect && (
          <select
            aria-label="Phone country"
            value={country}
            disabled={disabled}
            onChange={(e) => setCountry(e.target.value)}
            className={selectClassName || DEFAULT_SELECT_CLASS}
          >
            {MOBILE_COUNTRY_OPTIONS.map((opt) => (
              <option key={opt.code} value={opt.code}>{opt.label}</option>
            ))}
          </select>
        )}
        <input
          type="tel"
          inputMode={digitsOnly ? 'numeric' : 'tel'}
          id={id}
          name={name}
          value={value || ''}
          onChange={handleInput}
          onBlur={() => setTouched(true)}
          required={required}
          disabled={disabled}
          maxLength={maxLength || (digitsOnly ? rules.maxDigits : 20)}
          placeholder={placeholder || rules.hint}
          className={`flex-1 min-w-0 ${inputClassName || DEFAULT_INPUT_CLASS} ${borderClass}`}
        />
      </div>
      {shownError && <p className="text-xs text-red-500 mt-1">{shownError}</p>}
    </div>
  );
};

export default PhoneInput;
